/**
 * useReveal — the single scroll animation on this site.
 *
 * Assigns `.is-visible` to the ref once it enters the viewport, one time
 * only. Combined with `.rise-in` in CSS this produces a 10px lift + fade,
 * 400ms, ease-out, once. `prefers-reduced-motion` is honoured by the CSS
 * rule, not here.
 *
 * Usage:
 *   const ref = useReveal();
 *   return <div ref={ref} className="rise-in">...</div>;
 */
import { useEffect, useRef } from "react";

export function useReveal(threshold = 0.15) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Reduced-motion: mark visible immediately, skip the observer entirely.
    if (typeof window !== "undefined" &&
        window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      el.classList.add("is-visible");
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            io.unobserve(entry.target);
          }
        }
      },
      { threshold, rootMargin: "0px 0px -40px 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [threshold]);

  return ref;
}
